import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { Dialog } from "../../components/ui/Dialog";
import { EmptyState, Skeleton } from "../../components/ui/Feedback";
import { useToast } from "../../components/ui/Toast";
import { formatDate } from "../../lib/format";
import { useShopTimeZone } from "../settings/useSettings";
import type { CampaignType, ContentItem, ContentPlacement } from "../../lib/types";
import { assetUrl } from "../media/useMedia";
import { CONTENT_TYPES, contentTypeFor, type ContentType } from "./contentTypes";
import { PopupForm } from "./PopupForm";
import { TickerForm } from "./TickerForm";
import { useContentByPlacement, useDeleteContent } from "./useContent";

const PLACEMENTS = CONTENT_TYPES.map((type) => type.placement);

type Editing = { placement: ContentPlacement; item: ContentItem | null };

type ContentLibraryProps = {
	campaignId: string;
	campaignType: CampaignType;
	campaignStartsAt: string | null;
	campaignEndsAt: string | null;
};

const describeWindow = (item: ContentItem, timeZone: string): string => {
	if (!item.startsAt && !item.endsAt) return "Runs for the whole campaign";
	return `${item.startsAt ? formatDate(item.startsAt, timeZone) : "Campaign start"} → ${
		item.endsAt ? formatDate(item.endsAt, timeZone) : "Campaign end"
	}`;
};

const TypePicker = ({
	open,
	onOpenChange,
	taken,
	onPick,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	taken: ReadonlySet<ContentPlacement>;
	onPick: (type: ContentType) => void;
}) => (
	<Dialog
		open={open}
		onOpenChange={onOpenChange}
		title="Add content"
		description="Pick what shoppers should see while this campaign runs."
	>
		<div className="space-y-2">
			{CONTENT_TYPES.map((type) => {
				const used = !type.multiple && taken.has(type.placement);

				return (
					<button
						key={type.placement}
						type="button"
						disabled={used}
						onClick={() => onPick(type)}
						className="flex w-full items-start gap-3 rounded-md border border-line bg-surface px-3 py-3 text-left transition-colors hover:border-line-strong hover:bg-raised disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-line disabled:hover:bg-surface"
					>
						<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-raised">
							<type.icon className="h-4 w-4 text-ink-muted" strokeWidth={1.75} />
						</span>
						<span className="min-w-0 space-y-0.5">
							<span className="flex items-center gap-2">
								<span className="text-sm font-medium text-ink">{type.label}</span>
								{used ? <Badge variant="quiet">Added</Badge> : null}
							</span>
							<span className="block text-xs text-ink-muted">{type.description}</span>
						</span>
					</button>
				);
			})}
		</div>
	</Dialog>
);

export const ContentLibrary = ({
	campaignId,
	campaignType,
	campaignStartsAt,
	campaignEndsAt,
}: ContentLibraryProps) => {
	const { timeZone } = useShopTimeZone();
	const queries = useContentByPlacement(campaignId, PLACEMENTS);
	const removePopup = useDeleteContent(campaignId, "popup");
	const removeTicker = useDeleteContent(campaignId, "floating_ticker");
	const confirm = useConfirm();
	const toast = useToast();

	const [picking, setPicking] = useState(false);
	const [editing, setEditing] = useState<Editing | null>(null);

	const loading = queries.some((query) => query.isPending);
	const rows = PLACEMENTS.flatMap((placement, index) =>
		(queries[index]?.data?.items ?? []).map((item) => ({ placement, item })),
	);
	const taken = new Set(rows.map((row) => row.placement));

	const destroy = (placement: ContentPlacement, item: ContentItem) => {
		const remover = placement === "popup" ? removePopup : removeTicker;
		confirm({
			title: `Delete “${item.title}”?`,
			description:
				placement === "popup"
					? "The image stays in your library."
					: "The ticker stops showing on the storefront after the next publish.",
			onConfirm: () =>
				remover.mutate(item.id, {
					onSuccess: () => toast.push("Content deleted"),
					onError: (error) => toast.push(error.message, "error"),
				}),
		});
	};

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<p className="max-w-md text-xs text-ink-muted">
					Everything shoppers see while this campaign runs. Each piece goes live at the next
					publish.
				</p>
				<Button variant="outline" size="sm" onClick={() => setPicking(true)}>
					<Plus className="h-3.5 w-3.5" strokeWidth={2} />
					Add content
				</Button>
			</div>

			{loading ? <Skeleton className="h-32 w-full" /> : null}

			{!loading && rows.length === 0 ? (
				<EmptyState
					title="No content yet"
					description="Add a popup or a floating ticker to promote this campaign on the storefront."
					action={
						<Button variant="outline" size="sm" onClick={() => setPicking(true)}>
							<Plus className="h-3.5 w-3.5" strokeWidth={2} />
							Add content
						</Button>
					}
				/>
			) : null}

			<div className="space-y-2">
				{rows.map(({ placement, item }) => {
					const type = contentTypeFor(placement);

					return (
						<div
							key={item.id}
							className="flex items-center gap-3 rounded-md border border-line bg-surface px-3 py-2.5"
						>
							{item.desktopAssetId ? (
								<img
									src={assetUrl(item.desktopAssetId)}
									alt=""
									loading="lazy"
									className="h-11 w-14 shrink-0 rounded-sm bg-raised object-contain"
								/>
							) : (
								<span className="flex h-11 w-14 shrink-0 items-center justify-center rounded-sm bg-raised">
									{type ? (
										<type.icon className="h-4 w-4 text-ink-muted" strokeWidth={1.75} />
									) : null}
								</span>
							)}

							<div className="min-w-0 flex-1">
								<div className="flex flex-wrap items-center gap-2">
									<p className="truncate text-sm font-medium text-ink">{item.title}</p>
									<Badge variant="outline">{type?.label ?? placement}</Badge>
								</div>
								<p className="mt-0.5 text-xs text-ink-faint">{describeWindow(item, timeZone)}</p>
							</div>

							<div className="flex shrink-0 gap-1">
								<Button variant="ghost" size="sm" onClick={() => setEditing({ placement, item })}>
									Edit
								</Button>
								<Button
									variant="ghost"
									size="sm"
									aria-label={`Delete ${item.title}`}
									onClick={() => destroy(placement, item)}
								>
									<Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
								</Button>
							</div>
						</div>
					);
				})}
			</div>

			<TypePicker
				open={picking}
				onOpenChange={setPicking}
				taken={taken}
				onPick={(type) => {
					setPicking(false);
					setEditing({ placement: type.placement, item: null });
				}}
			/>

			<PopupForm
				campaignId={campaignId}
				campaignStartsAt={campaignStartsAt}
				campaignEndsAt={campaignEndsAt}
				item={editing?.item ?? null}
				open={editing?.placement === "popup"}
				onClose={() => setEditing(null)}
			/>

			<TickerForm
				campaignId={campaignId}
				campaignType={campaignType}
				campaignEndsAt={campaignEndsAt}
				item={editing?.item ?? null}
				open={editing?.placement === "floating_ticker"}
				onClose={() => setEditing(null)}
			/>
		</div>
	);
};
